import * as React from 'react';
import withStyles from 'react-jss';
import * as  moment from 'moment';
import { CssType, ThemeType } from './theming/jssTypes';
import { withRouter } from 'react-router';
import './App.css';
import 'antd/dist/antd.less';
import { loadInitialData } from './actions/loadInitialData';
import { connect } from 'react-redux';
// Lazy-loaded: SideMenu pulls in 323 KB NavbarMenuIcons; modals only render on demand.
const SideMenu = React.lazy(() => import('./components/sidemenu/SideMenu'));
import Loader from 'src/design-system/components/molecules/Loader/Loader';
import * as LogRocket from 'logrocket';
import setupLogRocketReact from 'logrocket-react';
import {
  LOG_ROCKET_KEY, ENVIRONMENT, RENDER_PERSISTENT_IFRAME_OPS,
  SENTRY_KEY, VERSION, IS_ENV_PRODUCTION, USERPILOT_TOKEN,
  LOG_ROCKET_CONFIG,
} from './globals';
import * as Sentry from '@sentry/browser';
import Router from './Router';

import { getUsecaseType } from './selectors/masterdata';
import PersistentIframeInjector from './components/common/persistentIframeRenderer/PersistentIframeInjector';
import { IframeApps } from './components/common/persistentIframeRenderer/constants';
import { setCurrHub } from './actions/cachedDataActions';
import { withTranslation } from 'react-i18next';
import { isLanguageRTL } from './utils/intl-utils';
import { setRTL } from './actions/uiThemeActions';

const UserConsentModal = React.lazy(() => import('./components/common/UserConsentModal'));
const PasswordWarning = React.lazy(() => import('./components/pages/PasswordPolicy/PasswordWarning'));
const ChangePasswordModal = React.lazy(() => import('./components/common/navbarModals/changePassword/changePasswordModal'));
import PaymentOverdueBanner from './components/common/PaymentOverdueBanner';
import ScheduledMaintenanceBanner from './components/common/ScheduledMaintenanceBanner';
import { getDevToolsRoute } from './routing/utils';
import * as globals from './globals';
import { notification } from 'antd';
import { handleSignInWithActivationLink } from './utils/app-utils';
import { initializeGa } from './utils/googleAnalyticsHelper';
import { userpilotInitialize } from './utils/userpilotHelper';
import { isAuthenticationHeadersPresent, redirectToLoginPage, isAuthenticationFlowPage } from './api/utils';
import { initEmbeddedWebview, isEmbeddedWebview } from './utils/embeddedWebview';
import { waitForBridge } from './utils/nativeBridge';
import DevrevWidget from './components/common/DevrevWidget';
import { OlvyWidget } from '@olvyhq/widget-react';
import { LocalStorageKeys, LocalStorageService } from './services/localStorage';
import Cookies from 'universal-cookie';
import OpsDashboardIframe from './components/common/persistentIframeRenderer/OpsDashboardIFrame';
const AddPickupModal = React.lazy(() => import('./components/common/AddPickupModal')) as React.ComponentType<any>;
import { setShowAddPickup } from './actions/opsDashboardStateActions';

const styles = (theme: ThemeType): CssType => ({
  mainDiv: {
    direction: (props) => isLanguageRTL(props.i18n.language) ? 'rtl' : 'ltr',
  },
  contentDiv: {
    height: '100%',
    overflow: 'auto',
    backgroundColor: theme.colors.pageBg,
  },
  bannerDiv:{
    position: 'relative',
    top: '-115px',
    maxWidth: '80%',
    width: 'fit-content',
    borderRadius: '15px',
    display: 'flex',
    justifyContent: 'space-around',
    margin: 'auto',
    zIndex: 999,
    overflow: 'hidden',
  },
});
const allowedViewTypesIframeOps = [
  'logistics',
  'retail',
  'domestic',
  'tms',
  'logistics_express',
  'ondemand',
];
const cookies = new Cookies();
class App extends React.Component<any, any> {

  state = {
    overdueBannerClosed: false,
    maintenanceBannerClosed: false,
    isConsentGiven: false,
    passwordWarningClosed: false,
    isChangePassowrdModalVisible: false,
    userId: null,
    organisationId: null,
    scheduledMaintenanceBannerCloseExpired: false,
    sideMenuEverOpened: false,
  };

  // SideMenu is lazy; once opened it stays mounted so reopening is instant.
  static getDerivedStateFromProps(props, state) {
    if (props.sideMenuOpen && !state.sideMenuEverOpened) {
      return { sideMenuEverOpened: true };
    }
    return null;
  }

  handleNativeBack: (() => void) | null = null;

  checkSignInFromInternal = async () => {
    const searchParams = new URLSearchParams(this.props.location?.search);
    const decodedAccessToken = searchParams?.has('accessToken') ? window.atob(searchParams.get('accessToken')) : '';
    const decodedAccessTokenExpiry = searchParams?.has('tokenExpiry') ? window.atob(searchParams.get('tokenExpiry')): '';
    const decodedOrganisationId = searchParams?.has('organisationId') ? window.atob(searchParams.get('organisationId')): '';
    const decodedEmployeeId = searchParams?.has('employeeId') ? window.atob(searchParams.get('employeeId')): '';
    if (
      decodedAccessToken &&
      decodedAccessTokenExpiry &&
      decodedEmployeeId &&
      decodedOrganisationId
    ) {
      await LocalStorageService.set(LocalStorageKeys.__AUTH_TOKEN__, decodedAccessToken);
      await LocalStorageService.set(LocalStorageKeys.___USER_ID__, decodedEmployeeId);
      await LocalStorageService.set(LocalStorageKeys.__ORGANISATION_ID__, decodedOrganisationId);
      cookies.set('is_authenticated', 'true', {
        path:'/',
        expires: new Date((new Date().getTime()) + (1000 * 60 * 60 * 24 * 365)) ,
        secure: true,
        sameSite: 'lax',
      });
      window.location.href = '/';
    }
  };

  handleSignInUsingActivationLink = async () => {
    await handleSignInWithActivationLink(this.props.location);
  };

  async componentDidMount() {
    const favicon_url = await LocalStorageService.get(LocalStorageKeys.__FAVICON_URL__);
    const favElement: any = document.getElementById('favicon');
    if(favicon_url && typeof favicon_url === 'string'){
      favElement.href = favicon_url;
    }
    const { fetchingData, dispatch, location, i18n } = this.props;
    if (!fetchingData) return;

    if (this.props.location?.pathname?.includes('signInFromInternal')) {
      await this.checkSignInFromInternal();
    }

    if (this.props.location?.pathname === '/employeeActivation') {
      await this.handleSignInUsingActivationLink();
    }

    // Initialize embedded webview credentials from URL params
    await initEmbeddedWebview();

    if (isEmbeddedWebview()) await waitForBridge();

    if (isAuthenticationHeadersPresent() && !isAuthenticationFlowPage()) {
      await dispatch(loadInitialData(location.pathname));
    } else if (!isAuthenticationFlowPage()) {
      redirectToLoginPage();
    } else {
      return;
    }

    await this.initLogRocket();
    await this.initSentry();

    // Handle RTL theme set
    this.setUIDirection(i18n.language);
    i18n.on('languageChanged', (lng) => {
      this.setUIDirection(lng);
    });

    // Add event listener for change in current hub from iframe
    window.addEventListener('storage', (event) => {
      if (event.key !== 'currentHub') {
        return;
      }
      try {
        const oldValue = JSON.parse(event.oldValue || '{}');
        const newValue = JSON.parse(event.newValue || '{}');
        if (oldValue.id !== newValue.id) {
          dispatch(setCurrHub({ currHub: newValue }, true));
        }
      } catch (e) { }
    });
    this.showConfigWarning();
    initializeGa();
    if(IS_ENV_PRODUCTION){
      userpilotInitialize(this.props.username);
    }
    import('./utils/notificationServiceHelper').then(({ notificationInitialisation }) => {
      notificationInitialisation(dispatch);
    }).catch((e) => {
      console.error('Failed to load notificationServiceHelper', e);
    });
    this.setState({
      userId: await LocalStorageService.get(LocalStorageKeys.___USER_ID__),
      organisationId: await LocalStorageService.get(LocalStorageKeys.__ORGANISATION_ID__),
      scheduledMaintenanceBannerCloseExpired: (await LocalStorageService.get(LocalStorageKeys.__SHOW_SCHEDULED_MAINTAINENCE_BANNER__) || 'true') === 'true',
    });
    await this.inilitiazeLiaNativeApp();
  }

  showConfigWarning() {
    const { history } = this.props;
    if (!globals.IS_FINAL_CONFIG_EQUAL) {
      notification.warning({
        message: 'Warning!',
        description: 'Your config file differs from the default one. App may behave differently. To avoid this, navigate to Dev Tools or click in this notification box',
        duration: 6,
        placement: 'bottomRight',
        style: { cursor: 'pointer' },
        onClick: () => history.push(getDevToolsRoute()),
      });
    }
  }

  setUIDirection = (lng) => {
    const { dispatch } = this.props;
    const isRTL = isLanguageRTL(lng);
    dispatch(setRTL(isRTL));
  };

  initLogRocket = async () => {
    if (LOG_ROCKET_KEY) {
      const userId: any = this.props.userData.username;
      const orgId: any = await LocalStorageService.get(LocalStorageKeys.__ORGANISATION_ID__);
      const name = `${orgId}_${this.props.userData.employee_code}_${this.props.userData.username}`;
      localStorage.setItem('userName', userId);
      if (LOG_ROCKET_CONFIG[orgId]?.includes(userId)) {
        LogRocket.init(LOG_ROCKET_KEY, {
          mergeIframes: true,
          childDomains: ['*']
        });
        LogRocket.identify(userId, {
          name,
          email: this.props.userData.email,
        });
        setupLogRocketReact(LogRocket);
      }
    }
  };
  initSentry = async () => {
    if (SENTRY_KEY) {
      const userId: any = this.props.userData.username;
      const orgId: any = await LocalStorageService.get(LocalStorageKeys.__ORGANISATION_ID__);
      const name = `${orgId}_${this.props.userData.employee_code}_${this.props.userData.username}`;
      Sentry.init({ dsn: SENTRY_KEY, release: VERSION, environment: 'production' });
      Sentry.configureScope((scope) => {
        scope.setUser({
          orgId,
          userId,
          email: this.props.userData.email,
          username: name,
        });
      });
    }
  };

  inilitiazeLiaNativeApp = async () => {
    if (window.ReactNativeWebView) {
      const userId = await LocalStorageService.get(LocalStorageKeys.___USER_ID__);
      const organisationId = await LocalStorageService.get(LocalStorageKeys.__ORGANISATION_ID__);
      const authToken = await LocalStorageService.get(LocalStorageKeys.__AUTH_TOKEN__);
      window.ReactNativeWebView.postMessage(JSON.stringify({ userId, organisationId, authToken }));

      this.handleNativeBack = () => {
        const { history } = this.props;
        if (history) {
          history.goBack();
        }
      };
      window.addEventListener('lia-native-back', this.handleNativeBack as EventListener);
    }
  };

  closeConsentModal = () => {
    this.setState({
      isConsentGiven: true,
    });
  };

  closeChangePasswordModal = () => {
    this.setState({
      isChangePassowrdModalVisible: false,
    });
  };

  closePasswordWarningModal = (type) => {
    if(type === 'skip'){
      this.setState({
        passwordWarningClosed: true,
      });
    } else {
      this.setState({
        passwordWarningClosed: true,
        isChangePassowrdModalVisible: true,
      });
    }
  };
  closeOverdueBanner = () => {
    this.setState({
      overdueBannerClosed: true,
    });
  };

  closeMaintenanceBanner = () => {
    this.setState({
      maintenanceBannerClosed: true,
    });
    LocalStorageService.set(LocalStorageKeys.__SHOW_SCHEDULED_MAINTAINENCE_BANNER__, 'false');
  };

  getTypeForPasswordWarning = () => {
    const { isFirstLogin, emailActivationOption, allowSkipPasswordWarning } = this.props;

    if (allowSkipPasswordWarning) {
      return 'warning';
    }

    if (emailActivationOption && isFirstLogin) {
      return 'firstLogin';
    }

    return 'expired';
  };

  render() {
    const { fetchingData } = this.props;
    if (fetchingData && !isAuthenticationFlowPage()) {
      return <Loader />;
    }
    const { classes, isOpsIframeLoaded, viewType, showUserConsent,
      showHybridView, paymentConfig, scheduledMaintenanceConfig,
      showPasswordWarning, allowSkipPasswordWarning, showAddPickup } = this.props;
    const { isConsentGiven, overdueBannerClosed, maintenanceBannerClosed,
      passwordWarningClosed, isChangePassowrdModalVisible, scheduledMaintenanceBannerCloseExpired, userId,
      organisationId, sideMenuEverOpened } = this.state;
    const showPaymentOverdueBanner = (paymentConfig?.banner_status ?? false)&& !overdueBannerClosed;
    const beforeMaintenanceEndTime = scheduledMaintenanceConfig
      && scheduledMaintenanceConfig.end_time
      ? moment(scheduledMaintenanceConfig.end_time).isAfter()
      : false;
    const showScheduledMaintenanceBanner = beforeMaintenanceEndTime
      && scheduledMaintenanceBannerCloseExpired && !maintenanceBannerClosed;
    const isConsentModalVisible = !isConsentGiven && showUserConsent;
    const isPasswordWarningVisible = !passwordWarningClosed && showPasswordWarning;

    return (
      <div className={classes.mainDiv} style={{ height: isAuthenticationFlowPage() ? 0 : '100%' }}>
       <OlvyWidget
          config={{ workspaceAlias: 'shipsy',
            options: {
              user: {
                name: this.props.userData.name,
                email: this.props.userData.email,
                identifier: userId,
                tags: [organisationId],
              },
            },
          }}
          targetElement={<div></div>}
        ></OlvyWidget>
        {isConsentModalVisible &&
          <React.Suspense fallback={null}>
            <UserConsentModal
              onModalClose={this.closeConsentModal}
              isVisible={isConsentModalVisible} />
          </React.Suspense>}

        {showAddPickup &&
          <React.Suspense fallback={null}>
            <AddPickupModal
              onModalClose={() => this.props.dispatch(setShowAddPickup(false))}
            />
          </React.Suspense>}
        {isPasswordWarningVisible &&
          <React.Suspense fallback={null}>
            <PasswordWarning
              isVisible={isPasswordWarningVisible}
              type={this.getTypeForPasswordWarning()}
              handleSkip={() => this.closePasswordWarningModal('skip')}
              handleChangePassword={() => this.closePasswordWarningModal('change')}
            />
          </React.Suspense>}
        {
          isChangePassowrdModalVisible &&
            <React.Suspense fallback={null}>
              <ChangePasswordModal
                hideCloseButton={!allowSkipPasswordWarning}
                isModalActive={isChangePassowrdModalVisible}
                onModalClose={this.closeChangePasswordModal}
              />
            </React.Suspense>
        }
        {/* zIndex={1}: default Loader is zIndex:-1 (fine for the full-screen gate, but
            hidden behind content here), leaving a blank screen while a lazy route loads. */}
        <React.Suspense fallback={<Loader zIndex={1} />}>
          <div className={classes.contentDiv}>
            <Router {...this.props} />
          </div>
        </React.Suspense>
        {!isEmbeddedWebview() && sideMenuEverOpened &&
          <React.Suspense fallback={null}>
            <SideMenu />
          </React.Suspense>}
        <div className={classes.bannerDiv}>
        {showPaymentOverdueBanner &&
          <PaymentOverdueBanner
            paymentConfig={paymentConfig}
            onBannerClose={this.closeOverdueBanner}
          />
        }
        {showScheduledMaintenanceBanner &&
          <ScheduledMaintenanceBanner
            scheduledMaintenanceConfig={scheduledMaintenanceConfig}
            onBannerClose={this.closeMaintenanceBanner}
          />
        }
        </div>
        <DevrevWidget />
        <PersistentIframeInjector appName={IframeApps.GENERIC_ANALYTICS} topRenderDistance='0px' />
        <PersistentIframeInjector appName={IframeApps.API_PLAYGROUND} />
        <PersistentIframeInjector appName={IframeApps.LIA_CO_PILOT} />
        <PersistentIframeInjector appName={IframeApps.SHIPSY_AGENT} />
        {(this.props.customFrontendManifest?.apps || []).map((a) => (
          <PersistentIframeInjector key={a.appId} appName={a.appId} />
        ))}
        <OpsDashboardIframe appName={IframeApps.OpsDashboard} />
        {/* DONT REMOVE THIS, REQUIRED FOR GENERIC BULK UPLOAD SERVICE */}
        <div id='generic-bulk-upload-service-container'></div>
      </div>
    );
  }
}

const mapStateToProps = (state) => {
  const { initializing, masterData, persistentIframe, opsDashboardStateReducer, navigation } = state;
  return {
    fetchingData: initializing,
    showAddPickup: opsDashboardStateReducer.showAddPickup,
    userData: masterData.user_data || {},
    usecaseType: getUsecaseType(masterData),
    viewType: masterData.viewType,
    isOpsIframeLoaded: persistentIframe[IframeApps.OpsDashboard].isLoaded,
    // isOpsIframeLoaded: false,
    showUserConsent: masterData.ops_dashboard_config?.parts_to_show?.show_consent,
    showPasswordWarning: masterData.password_expiry?.should_send,
    allowSkipPasswordWarning: masterData.password_expiry?.allow_skip,
    showHybridView: masterData.show_v1_hybrid_view,
    paymentConfig: masterData.ops_dashboard_config?.config?.payment_config,
    scheduledMaintenanceConfig:
      masterData.ops_dashboard_config?.config?.scheduled_maintenance_config,
    emailActivationOption: masterData.employee_email_activation_option,
    isFirstLogin: masterData.user_data?.is_first_login,
    isSuperAdmin: masterData.ops_dashboard_config?.parts_to_show?.super_admin,
    username: masterData.user_data?.username,
    customFrontendManifest: masterData.custom_frontend_manifest,
    sideMenuOpen: navigation.sideMenuOpen,
  };

};

export default withTranslation()(withRouter(
  connect(mapStateToProps)(withStyles(styles,
    { injectTheme: true })(App))) as React.ComponentType<any>);
